/**
 * Olloport backend integration.
 *
 * Lets the editor open a scene straight from the backend instead of a local
 * file — the missing piece for a remote/SSH setup where the browser can't see
 * the workstation's filesystem.
 *
 * Flow: the dashboard links here as `editor.olloport.com/?scene=<id>`. On
 * startup we pull that scene's editable `.ssproj` from
 * `GET /admin/editor-scenes/:id/project` and hand it to the existing
 * `doc.load` path.
 *
 * Auth: the in-editor "Sign in to Olloport" dialog logs in with the user's
 * email + password and stores the returned session id, which is sent as
 * `Authorization: Bearer`. This works on the deployed editor AND on localhost
 * (where the cross-site `SameSite=Lax` cookie can't be sent). The deployed
 * editor also still rides the `.olloport.com` cookie via credentials:'include';
 * a `localStorage` `olloport_admin_key` remains as a Bearer fallback for
 * scripts / legacy local dev.
 */

import type { Entity } from 'playcanvas';

import { devOnlyBase } from './dev-host';
import type { Events } from '../events';
import type { Splat } from '../splat';
import type { Model } from './model';

const params = new URLSearchParams(window.location.search);

// Fixed in production; the ?apiBase / ?authBase / ?viewerBase overrides are
// honoured only when the editor is itself running on localhost. Every request
// below carries the user's session, and `login` POSTs their password to
// AUTH_BASE — see dev-host.ts for why a link must never get to choose where
// those go.
const API_BASE = devOnlyBase(params, 'apiBase', 'https://api.olloport.com');
const AUTH_BASE = devOnlyBase(params, 'authBase', 'https://auth.olloport.com');
const VIEWER_BASE = devOnlyBase(params, 'viewerBase', 'https://viewer.olloport.com');

export interface OlloUser {
    id: string;
    email: string;
    role: string;
}

// Tracks whether the splat SOURCE (geometry / LOD) changed since the scene was
// opened or last source-saved. Markers/camera/thumbnail/walk do NOT flip this —
// they ride the cheap editor-meta.json sidecar instead. So a metadata-only save
// skips re-serializing + re-uploading the heavy .ssproj (the slow part).
// Defaults true so a brand-new session's first save always ships the source.
let sourceDirty = true;

const TOKEN_KEY = 'olloport_session_token';

// ---- open-scene binding -------------------------------------------------
//
// Which backend scene "Save to Olloport" writes into. This is bound to the
// OPEN DOCUMENT — never to the browser:
//
//  * sessionStorage, not localStorage: a new editor window/tab opens with
//    nothing bound, so it can't inherit a scene opened days ago elsewhere.
//  * cleared on every document reset/replace (scene.clear): File ▸ New or
//    loading a different .ssproj publishes as a NEW scene rather than
//    replacing whatever happened to be open before.
//
// Set in exactly two places: openSceneFromBackend (?scene=<id>) and
// create-on-first-save. Both run AFTER the load's scene.clear, so the
// listener below can't wipe a binding we just made.
//
// This stickiness is not theoretical: a browser-scoped binding is how a
// different project got saved over a customer's scan on 2026-07-15,
// replacing it in place, and how every asset upload then 404'd against the
// deleted scene it still pointed at.
const OPEN_SCENE_KEY = 'olloport_open_scene_id';

const openSceneId = (): string | null => {
    try {
        return sessionStorage.getItem(OPEN_SCENE_KEY);
    } catch {
        return null;
    }
};

const setOpenSceneId = (id: string | null): void => {
    try {
        if (id) sessionStorage.setItem(OPEN_SCENE_KEY, id);
        else sessionStorage.removeItem(OPEN_SCENE_KEY);
    } catch { /* ignore */ }
};

// Evict the legacy browser-scoped binding. Deliberately NOT migrated into
// the session store — its stickiness is the bug being fixed.
try {
    localStorage.removeItem(OPEN_SCENE_KEY);
} catch { /* ignore */ }

// The in-editor sign-in stores the session id here and sends it as a Bearer —
// the auth path that works on localhost (where the cross-site SameSite=Lax
// cookie is never sent) AND on the deployed editor.
const sessionToken = (): string | null => {
    try { return localStorage.getItem(TOKEN_KEY); } catch { return null; }
};
const setSessionToken = (token: string | null): void => {
    try {
        if (token) localStorage.setItem(TOKEN_KEY, token);
        else localStorage.removeItem(TOKEN_KEY);
    } catch { /* ignore */ }
};

// Legacy / local-dev fallback: an admin API key pasted into localStorage.
const adminKey = (): string | null => {
    try {
        return localStorage.getItem('olloport_admin_key');
    } catch {
        return null;
    }
};

// Bearer credential, preferring the in-editor session token over an admin key.
const bearer = (): string | null => sessionToken() ?? adminKey();

// Send the session cookie (deployed) and, if present, a Bearer credential
// (in-editor login token, or a local-dev admin key).
const authedFetch = (url: string, init: RequestInit = {}): Promise<Response> => {
    const headers = new Headers(init.headers);
    const key = bearer();
    if (key) headers.set('Authorization', `Bearer ${key}`);
    return fetch(url, { ...init, credentials: 'include', headers });
};

/**
 * authedFetch with retries, for the upload path.
 *
 * A publish pushes ~100 MB across a hundred requests after a LOD build that
 * can take ten minutes. Without retries a single dropped connection — which
 * surfaces as a bare "Failed to fetch" — threw all of that away and the whole
 * save had to start over. Transient failures (network error, 429, 5xx) are
 * retried with backoff; a real rejection (4xx) returns immediately so the
 * caller's own handling, like the 409 replace confirmation, still runs.
 *
 * Every request here is a PUT/POST that overwrites or addresses one specific
 * object, so repeating one is harmless.
 */
const retryFetch = async (
    url: string,
    init: RequestInit,
    label: string,
    events: Events,
    attempts = 4
): Promise<Response> => {
    let lastErr: unknown = null;
    for (let attempt = 1; attempt <= attempts; attempt++) {
        try {
            const res = await authedFetch(url, init);
            if (res.ok || (res.status < 500 && res.status !== 429)) return res;
            lastErr = new Error(`${res.status}`);
        } catch (err) {
            lastErr = err;   // network-level failure ("Failed to fetch")
        }
        if (attempt < attempts) {
            const waitMs = 800 * Math.pow(2, attempt - 1);   // 0.8s, 1.6s, 3.2s
            events.fire('spinner.text', `${label} — connection dropped, retrying (${attempt}/${attempts - 1})…`);
            await new Promise(resolve => setTimeout(resolve, waitMs));
        }
    }
    const detail = lastErr instanceof Error ? lastErr.message : String(lastErr);
    throw new Error(`${label} failed after ${attempts} attempts (${detail}). Nothing was lost — press save again to resume.`);
};

export const getSession = async (): Promise<OlloUser | null> => {
    try {
        const res = await authedFetch(`${AUTH_BASE}/me`);
        if (!res.ok) return null;
        const data = await res.json() as { ok?: boolean; user?: OlloUser };
        return data.ok && data.user ? data.user : null;
    } catch {
        return null;
    }
};

export interface LoginResult {
    ok: boolean;
    user?: OlloUser;
    error?: string;
}

// Sign in with the user's olloport.com email + password. Stores the returned
// session token for Bearer auth; credentials:'include' also sets the cookie on
// the deployed editor. Driven by the in-editor login dialog.
export const login = async (email: string, password: string): Promise<LoginResult> => {
    try {
        const res = await fetch(`${AUTH_BASE}/login`, {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email, password })
        });
        const data = await res.json().catch(() => ({})) as
            { ok?: boolean; user?: OlloUser; token?: string; error?: string };
        if (!res.ok || !data.ok) {
            return { ok: false, error: data.error || `login failed (${res.status})` };
        }
        if (data.token) setSessionToken(data.token);
        return { ok: true, user: data.user };
    } catch (err) {
        return { ok: false, error: err instanceof Error ? err.message : String(err) };
    }
};

// Sign out: best-effort server session delete + clear the local token.
export const logout = async (): Promise<void> => {
    try { await authedFetch(`${AUTH_BASE}/logout`, { method: 'POST' }); } catch { /* best-effort */ }
    setSessionToken(null);
};

// Ensure the editor is authenticated. True if already signed in, or after the
// user completes the in-editor login dialog. Replaces the old bounce to
// app.olloport.com (which dead-ended on localhost).
const ensureSignedIn = async (events: Events): Promise<boolean> => {
    if (await getSession()) return true;
    return !!(await events.invoke('show.loginDialog'));
};

type StoredTransform = { position?: number[]; rotation?: number[]; scale?: number[] };
type StoredScene = {
    name?: string;
    scene_json?: {
        splats?: { id?: string; url?: string; transform?: StoredTransform }[];
        models?: { id?: string; url?: string; transform?: StoredTransform }[];
        walk?: { voxels?: { url?: string; transform?: StoredTransform } } & Record<string, unknown>;
    };
};

/**
 * Reopen a scene from its stored files: every splat's LOD (lod-meta.json),
 * its models and its collision voxels, then the metadata sidecar on top
 * (transforms, walk, markers, camera, timeline). The LOD is the source of a
 * scene whose splats aren't edited after upload, so saves send no .ssproj
 * (scene.lodSourceOnly). False when the scene isn't all LOD, so the caller
 * falls back to the .ssproj.
 */
const openSceneFromAssets = async (events: Events, sceneId: string): Promise<boolean> => {
    const id = encodeURIComponent(sceneId);
    const res = await authedFetch(`${API_BASE}/admin/editor-scenes/${id}`);
    if (!res.ok) return false;
    const info = await res.json() as StoredScene;
    const doc = info.scene_json ?? {};
    const splats = doc.splats ?? [];
    if (splats.length === 0 || !splats.every(s => s.url?.endsWith('/lod-meta.json'))) return false;

    const assetUrl = (path: string) => (/^https?:/.test(path) ? path : `${API_BASE}/v/editor-scenes/${id}/${path}`);
    let meta: any = null;
    try {
        const metaRes = await authedFetch(`${API_BASE}/admin/editor-scenes/${id}/meta`);
        if (metaRes.ok) meta = await metaRes.json();
    } catch { /* no sidecar → scene.json's own transforms and walk */ }
    meta = meta ?? { walk: doc.walk ?? null };
    // the voxels load from the scene's stored copy, wherever they came from
    const voxels = doc.walk?.voxels;
    if (voxels?.url && meta.walk) {
        meta.walk = { ...meta.walk, voxels: { url: assetUrl(voxels.url), transform: voxels.transform ?? meta.walk.voxels?.transform } };
    }

    // start clean as doc.load does, and take the scene's name so the save
    // guard sees the same project
    events.fire('scene.clear');
    events.fire('doc.setName', info.name ?? null);
    for (let i = 0; i < splats.length; i++) {
        const [splat] = await events.invoke('import', [{ filename: 'lod-meta.json', url: assetUrl(splats[i].url!) }]) as Splat[];
        if (!splat) throw new Error(`could not load ${splats[i].url}`);
        // the sidecar's transforms and the next save's remote LOD dir both key on the name
        splat.name = meta.splatXforms?.[i]?.name ?? splats[i].id ?? splat.name;
        if (!meta.splatXforms) applyTransform(splat.entity, splats[i].transform);
    }
    for (const m of doc.models ?? []) {
        if (!m.url) continue;
        const model = await events.invoke('ollo.importModel', { filename: m.url.split('/').pop(), url: assetUrl(m.url) }) as Model;
        applyTransform(model.entity, m.transform);
    }
    await events.invoke('doc.applyMeta', meta);

    sourceDirty = false;
    setOpenSceneId(sceneId);
    events.fire('ollo.sceneId', sceneId);
    return true;
};

const applyTransform = (entity: Entity, t?: StoredTransform) => {
    if (!t) return;
    if (t.position) entity.setLocalPosition(t.position[0], t.position[1], t.position[2]);
    if (t.rotation) entity.setLocalEulerAngles(t.rotation[0], t.rotation[1], t.rotation[2]);
    if (t.scale) entity.setLocalScale(t.scale[0], t.scale[1], t.scale[2]);
};

/**
 * Fetch a scene's `.ssproj` from the backend and open it via the editor's
 * existing document loader — for scenes that aren't all LOD (older saves).
 */
export const openSceneFromBackend = async (events: Events, sceneId: string): Promise<void> => {
    events.fire('startSpinner');
    try {
        if (await openSceneFromAssets(events, sceneId)) return;
        const res = await authedFetch(`${API_BASE}/admin/editor-scenes/${encodeURIComponent(sceneId)}/project`);

        if (res.status === 401 || res.status === 403) {
            setSessionToken(null);
            await events.invoke('showPopup', {
                type: 'error',
                header: 'Sign-in required',
                message: 'Your Olloport session has expired. Sign in again (File ▸ Sign in to Olloport), then reload the scene.'
            });
            return;
        }
        if (res.status === 404) {
            await events.invoke('showPopup', {
                type: 'error',
                header: 'No editable project',
                message: `Scene "${sceneId}" has no .ssproj source stored on the backend yet. Publish it once (which uploads the source) before it can be reopened here.`
            });
            return;
        }
        if (!res.ok) {
            await events.invoke('showPopup', {
                type: 'error',
                header: 'Could not load scene',
                message: `The backend returned ${res.status} for scene "${sceneId}".`
            });
            return;
        }

        const blob = await res.blob();
        const file = new File([blob], `${sceneId}.ssproj`, { type: 'application/x-supersplat' });
        await events.invoke('doc.load', file);

        // Overlay the metadata sidecar (markers/camera/thumbnail/walk) on top of
        // whatever the .ssproj carried — the sidecar is the freshest, since a
        // metadata-only save updates it without rewriting the .ssproj. 404 (no
        // sidecar yet) just leaves the .ssproj's own metadata in place.
        try {
            const metaRes = await authedFetch(`${API_BASE}/admin/editor-scenes/${encodeURIComponent(sceneId)}/meta`);
            if (metaRes.ok) {
                const meta = await metaRes.json();
                await events.invoke('doc.applyMeta', meta);
            }
        } catch { /* sidecar is best-effort — fall back to .ssproj metadata */ }

        // Freshly loaded from the backend → the source matches what's stored.
        sourceDirty = false;

        // Remember which backend scene this maps to, so a future "Save to
        // Olloport" overwrites it rather than creating a duplicate. Set after
        // doc.load, whose scene.clear drops any previous binding.
        setOpenSceneId(sceneId);
        events.fire('ollo.sceneId', sceneId);
    } catch (err) {
        await events.invoke('showPopup', {
            type: 'error',
            header: 'Could not load scene',
            message: `Network error loading scene "${sceneId}": ${err instanceof Error ? err.message : String(err)}`
        });
    } finally {
        events.fire('stopSpinner');
    }
};

const CONTENT_TYPES: Record<string, string> = {
    json: 'application/json', webp: 'image/webp', glb: 'model/gltf-binary',
    ply: 'application/octet-stream', bin: 'application/octet-stream',
    jpg: 'image/jpeg', jpeg: 'image/jpeg', png: 'image/png', ktx2: 'image/ktx2'
};
const contentTypeFor = (path: string): string =>
    CONTENT_TYPES[path.split('.').pop()?.toLowerCase() ?? ''] ?? 'application/octet-stream';

/**
 * Which client a newly-created scene belongs to. The session doesn't model the
 * user→client mapping yet, so resolve it explicitly: ?client=<slug> on the
 * editor URL → localStorage `olloport_client_id` → 'admin' (the default seed).
 */
const resolveClientId = (): string => {
    const fromUrl = params.get('client');
    if (fromUrl) return fromUrl;
    try {
        const ls = localStorage.getItem('olloport_client_id');
        if (ls) return ls;
    } catch { /* ignore */ }
    return 'admin';
};

/**
 * Create-on-first-save: make a backend scene for the current project when none
 * is open, so a freshly-loaded local splat can go live without an admin
 * pre-creating it. Returns the new id (persisted) or null on failure/redirect.
 */
const createSceneOnBackend = async (events: Events): Promise<string | null> => {
    const name = ((events.invoke('doc.name') as string) || '').trim() || 'Untitled scene';
    const clientId = resolveClientId();
    try {
        const res = await authedFetch(`${API_BASE}/admin/editor-scenes`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ name, client_id: clientId })
        });
        if (res.status === 401 || res.status === 403) {
            setSessionToken(null);
            await events.invoke('showPopup', {
                type: 'error',
                header: 'Sign-in required',
                message: 'Your Olloport session has expired. Sign in again (File ▸ Sign in to Olloport), then Save.'
            });
            return null;
        }
        if (res.status === 404) {
            await events.invoke('showPopup', {
                type: 'error',
                header: 'No such client',
                message: `Client "${clientId}" doesn't exist on the backend. Set the right one with ?client=<slug> on the editor URL (or localStorage olloport_client_id), then Save again.`
            });
            return null;
        }
        if (!res.ok) {
            await events.invoke('showPopup', {
                type: 'error',
                header: 'Could not create scene',
                message: `The backend returned ${res.status} creating the scene.`
            });
            return null;
        }
        const { id } = await res.json() as { id: string };
        setOpenSceneId(id);
        events.fire('ollo.sceneId', id);
        sourceDirty = true; // brand-new scene → its source must be uploaded
        return id;
    } catch (err) {
        await events.invoke('showPopup', {
            type: 'error',
            header: 'Could not create scene',
            message: `Network error creating the scene: ${err instanceof Error ? err.message : String(err)}`
        });
        return null;
    }
};

// Upload the editable .ssproj source through the Worker. Small projects go in a
// single streamed PUT; larger ones (over Cloudflare's ~100 MB inbound cap) are
// chunked into an R2 multipart upload the Worker assembles. No presigned URL,
// no R2 bucket CORS — the same admin auth path as scene.json.
const SINGLE_PUT_LIMIT = 90 * 1024 * 1024;   // stay under CF's ~100 MB Worker inbound cap
const MPU_CHUNK = 64 * 1024 * 1024;          // equal-size parts (R2 requires it), >= 5 MiB

const uploadProjectSource = async (sceneId: string, ssproj: Uint8Array, events: Events): Promise<void> => {
    const total = ssproj.byteLength;
    const mb = (total / (1024 * 1024)).toFixed(1);

    if (total <= SINGLE_PUT_LIMIT) {
        events.fire('spinner.text', `Uploading splat source (${mb} MB)…`);
        const put = await retryFetch(`${API_BASE}/admin/editor-scenes/${sceneId}/project`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/x-supersplat' },
            body: ssproj as BodyInit
        }, `Splat source (${mb} MB)`, events);
        if (!put.ok) throw new Error(`source upload failed (${put.status})`);
        return;
    }

    // Multipart: equal MPU_CHUNK parts, last part = the remainder.
    const partCount = Math.ceil(total / MPU_CHUNK);
    const createRes = await retryFetch(
        `${API_BASE}/admin/editor-scenes/${sceneId}/project-mpu`, { method: 'POST' },
        'Starting splat source upload', events
    );
    if (!createRes.ok) throw new Error(`could not start source upload (${createRes.status})`);
    const { uploadId } = await createRes.json() as { uploadId: string };

    const parts: Array<{ partNumber: number; etag: string }> = [];
    for (let i = 0; i < partCount; i++) {
        const start = i * MPU_CHUNK;
        const chunk = ssproj.slice(start, Math.min(start + MPU_CHUNK, total));
        const partNumber = i + 1;
        events.fire('spinner.text', `Uploading splat source — part ${partNumber}/${partCount} (${mb} MB)…`);
        const pRes = await retryFetch(
            `${API_BASE}/admin/editor-scenes/${sceneId}/project-mpu?uploadId=${encodeURIComponent(uploadId)}&part=${partNumber}`,
            { method: 'PUT', headers: { 'Content-Type': 'application/octet-stream' }, body: chunk as BodyInit },
            `Splat source part ${partNumber}/${partCount}`, events
        );
        if (!pRes.ok) throw new Error(`source part ${partNumber}/${partCount} failed (${pRes.status})`);
        const { etag } = await pRes.json() as { etag: string };
        parts.push({ partNumber, etag });
    }

    events.fire('spinner.text', 'Finalizing splat source…');
    const compRes = await retryFetch(`${API_BASE}/admin/editor-scenes/${sceneId}/project-mpu/complete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ uploadId, parts })
    }, 'Finalizing splat source', events);
    if (!compRes.ok) throw new Error(`source finalize failed (${compRes.status})`);
};

/**
 * Save the current project back to the open backend scene.
 *
 * Pushes the viewer bundle (scene.json + assets) AND the .ssproj source through
 * the Worker — a single PUT, or an R2 multipart upload for sources over
 * Cloudflare's ~100 MB inbound cap. The .ssproj is written whenever the splat
 * source changed — markers/camera/thumbnail/walk live inside its document.json, so
 * they're what the editor reopens; a save that skipped it would persist those
 * edits to the viewer's scene.json but silently lose them on the next reopen.
 * The splat chunks are size-diffed against the backend manifest, so unchanged
 * splats are never resent.
 *
 * Requires a scene to have been opened from the backend (?scene=<id>).
 */
export const saveToOlloport = async (events: Events): Promise<void> => {
    // Make sure we're authenticated before doing any work — opens the in-editor
    // login dialog if not (no more bounce to app.olloport.com).
    if (!(await ensureSignedIn(events))) return;

    let sceneId = openSceneId();
    let justCreated = !sceneId;

    events.fire('startSpinner');
    try {
        // Create-on-first-save: nothing open yet → make a backend scene so a
        // freshly-loaded local splat can go live without an admin pre-creating
        // it. Persists the new id + flips sourceDirty so the source uploads.
        if (!sceneId) {
            sceneId = await createSceneOnBackend(events);
            if (!sceneId) return; // create failed / redirected — error already shown
        }
        // 1. Backend asset manifest FIRST — it powers two things:
        //    (a) the size-diff that skips re-uploading unchanged assets, and
        //    (b) Phase 1.5: the remote-LOD skip. When the splat source hasn't
        //        changed and the backend already holds this scene's LOD chunks
        //        (<dir>/lod-meta.json), collectBundle skips the expensive LOD
        //        rebuild AND emits no splat entries — scene.json keeps pointing
        //        at the existing remote chunks. A geometry edit (sourceDirty)
        //        rebuilds LOD as before so it can never go stale.
        events.fire('spinner.text', 'Preparing scene…');
        const remote: Record<string, number> = {};
        let remoteName: string | null = null;
        try {
            let r = await authedFetch(`${API_BASE}/admin/editor-scenes/${sceneId}`);
            if (r.status === 404) {
                // The bound scene is gone from the backend (deleted from the
                // admin panel). Saving on would 404 every single asset upload,
                // so re-bind to a fresh scene and publish there instead.
                setOpenSceneId(null);
                sceneId = await createSceneOnBackend(events);
                if (!sceneId) return; // create failed — error already shown
                justCreated = true;
                sourceDirty = true;
                r = await authedFetch(`${API_BASE}/admin/editor-scenes/${sceneId}`);
            }
            if (r.ok) {
                const meta = await r.json() as { name?: string; assets?: { path: string; size_bytes: number }[] };
                remoteName = meta.name ?? null;
                for (const a of meta.assets ?? []) remote[a.path] = a.size_bytes;
            }
        } catch { /* first save / no manifest → ship everything */ }

        // Guard: never silently overwrite a scene that holds a DIFFERENT
        // project. The tab binding is invisible state — it can point at a
        // scene the author forgot about (this is how a gallery save replaced
        // a customer's drone scan in place on 2026-07-15). A name mismatch
        // between the bound backend scene and the open document → ask.
        if (!justCreated && remoteName) {
            const docName = ((events.invoke('doc.name') as string | null) ?? '').trim();
            if (docName && remoteName.trim().toLowerCase() !== docName.toLowerCase()) {
                events.fire('stopSpinner');
                const ow = await events.invoke('showPopup', {
                    type: 'yesno',
                    header: 'Overwrite a different scene?',
                    message: `This tab is bound to the backend scene "${remoteName}", but you're saving "${docName}". Yes = REPLACE "${remoteName}" with this project. No = keep it and decide next.`
                });
                if (ow?.action !== 'yes') {
                    const asNew = await events.invoke('showPopup', {
                        type: 'yesno',
                        header: 'Save as a new scene?',
                        message: `Yes = publish "${docName}" as a brand-new scene ("${remoteName}" stays untouched). No = cancel this save entirely.`
                    });
                    events.fire('startSpinner');
                    if (asNew?.action !== 'yes') {
                        const err = new Error(`Save cancelled — "${remoteName}" was left untouched.`);
                        err.name = 'SaveCancelled';
                        throw err;
                    }
                    setOpenSceneId(null);
                    sceneId = await createSceneOnBackend(events);
                    if (!sceneId) return; // create failed — error already shown
                    justCreated = true;
                    sourceDirty = true;
                    remoteName = null;
                    for (const k of Object.keys(remote)) delete remote[k];
                } else {
                    events.fire('startSpinner');
                }
            }
        }
        const remoteLod = sourceDirty ? [] : Object.keys(remote)
        .map(p => /^(.+)\/lod-meta\.json$/.exec(p)?.[1])
        .filter((d): d is string => !!d);

        // Preserve backend-only media in scene.json: a reopened session has no
        // local hover-video (and possibly no thumbnail) bytes, but the files
        // are still on the backend — keep referencing them instead of silently
        // dropping them from the scene on a routine re-save.
        const remoteHas = {
            thumbnail: 'thumbnail.jpg' in remote,
            preview: 'preview.mp4' in remote
        };

        const { entries } = await events.invoke('scene.collectBundle', { remoteLod, remoteHas }) as { entries: Map<string, Uint8Array> };

        const totalEntries = entries.size;
        let entryIdx = 0;
        for (const [path, bytes] of entries) {
            entryIdx++;
            const kb = (bytes.byteLength / 1024).toFixed(0);
            if (path === 'scene.json') {
                events.fire('spinner.text', `Uploading scene.json (${entryIdx}/${totalEntries})…`);
                const putSceneJson = (confirmReplace: boolean) => retryFetch(
                    `${API_BASE}/admin/editor-scenes/${sceneId}/scene.json`,
                    {
                        method: 'PUT',
                        headers: {
                            'Content-Type': 'application/json',
                            ...(confirmReplace ? { 'X-Confirm-Replace': 'different-content' } : {})
                        },
                        body: bytes as BodyInit
                    },
                    'scene.json', events
                );
                let res = await putSceneJson(false);
                // 409 content_mismatch: the backend guards against a save that
                // would replace a scene's splat with a DIFFERENT one (this is
                // how a client's scan was destroyed once). Swapping the splat
                // deliberately — a re-capture of the same space — is a normal
                // thing to do and hits the same guard, so ask rather than dead-
                // end, and resend with the confirmation header on a yes.
                if (res.status === 409) {
                    let detail = '';
                    try {
                        const body = await res.clone().json() as { error?: string; message?: string };
                        if (body?.error === 'content_mismatch') detail = body.message ?? '';
                    } catch (err) { /* not the guard we know — fall through */ }
                    if (detail) {
                        events.fire('stopSpinner');
                        const r = await events.invoke('showPopup', {
                            type: 'yesno',
                            header: 'Replace this scene’s splat?',
                            message: `${detail}\n\nYes = replace the splat in this scene (its markers, walk area and camera path are kept). No = cancel the save.`
                        });
                        events.fire('startSpinner');
                        if (r?.action !== 'yes') {
                            const err = new Error('Save cancelled — the scene was left untouched.');
                            err.name = 'SaveCancelled';
                            throw err;
                        }
                        events.fire('spinner.text', 'Replacing scene.json…');
                        res = await putSceneJson(true);
                    }
                }
                if (!res.ok) throw new Error(`scene.json upload failed (${res.status})`);
            } else {
                if (remote[path] === bytes.byteLength) continue; // unchanged
                events.fire('spinner.text', `Uploading ${path} (${entryIdx}/${totalEntries}, ${kb} KB)…`);
                const res = await retryFetch(`${API_BASE}/admin/editor-scenes/${sceneId}/assets/${path}`, {
                    method: 'PUT', headers: { 'Content-Type': contentTypeFor(path) }, body: bytes as BodyInit
                }, `${path} (${entryIdx}/${totalEntries})`, events);
                if (!res.ok) throw new Error(`asset "${path}" upload failed (${res.status})`);
            }
        }

        // 2. Metadata sidecar → markers/camera/thumbnail/walk as a small JSON,
        //    written EVERY save (sub-MB, through the Worker). This is what makes
        //    a metadata tweak fast: it persists without touching the .ssproj.
        events.fire('spinner.text', 'Saving metadata…');
        const meta = await events.invoke('doc.serializeMeta') as Uint8Array;
        const metaRes = await retryFetch(`${API_BASE}/admin/editor-scenes/${sceneId}/meta`, {
            method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: meta as BodyInit
        }, 'Scene metadata', events);
        if (!metaRes.ok) throw new Error(`metadata save failed (${metaRes.status})`);

        // 3. .ssproj source → uploaded THROUGH the Worker (single PUT, or an R2
        //    multipart upload when it exceeds Cloudflare's ~100 MB inbound cap).
        //    No presigned URL / R2 CORS. ONLY when the splat geometry / LOD
        //    actually changed — re-serializing + uploading the full source is the
        //    slow part, so we skip it for metadata-only edits.
        //    Not when every splat ships as a LOD: the stored scene files are
        //    the source (openSceneFromAssets), a .ssproj would be the full
        //    uncompressed splat again (2 GB for 8M splats).
        if (sourceDirty && events.invoke('scene.lodSourceOnly')) sourceDirty = false;
        const wroteSource = sourceDirty;
        if (sourceDirty) {
            events.fire('spinner.text', 'Packing splat source…');
            const ssproj = await events.invoke('doc.serializeBytes') as Uint8Array;
            await uploadProjectSource(sceneId, ssproj, events);
            sourceDirty = false;
        }

        // 4. Prune superseded assets — anything the freshly-saved scene.json no
        //    longer references (the previous LOD dir, a replaced flat SOG).
        //    Without this every geometry re-save leaks the old chunks forever.
        //    keep = this save's bundle entries + every path the new scene.json
        //    mentions; LOD references ("<dir>/lod-meta.json") keep the whole
        //    dir so unchanged remote chunks survive metadata-only saves.
        //    Non-fatal: a prune failure must never fail a completed save.
        try {
            const sceneJsonBytes = entries.get('scene.json');
            if (sceneJsonBytes) {
                const keep = new Set<string>(entries.keys());
                const keepPrefixes = new Set<string>();
                const walk = (v: unknown) => {
                    if (typeof v === 'string') {
                        if (v.startsWith('http') || v.includes('..')) return;
                        const lod = /^(.+)\/lod-meta\.json$/.exec(v);
                        if (lod) keepPrefixes.add(`${lod[1]}/`);
                        else if (/^[a-zA-Z0-9._/-]{1,256}$/.test(v) && v.includes('.')) keep.add(v);
                    } else if (Array.isArray(v)) {
                        v.forEach(walk);
                    } else if (v && typeof v === 'object') {
                        Object.values(v).forEach(walk);
                    }
                };
                walk(JSON.parse(new TextDecoder().decode(sceneJsonBytes)));
                const pruneRes = await authedFetch(`${API_BASE}/admin/editor-scenes/${sceneId}/assets-prune`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ keep: [...keep], keepPrefixes: [...keepPrefixes] })
                });
                if (pruneRes.ok) {
                    const p = await pruneRes.json() as { pruned: number };
                    if (p.pruned > 0) console.log(`[save] pruned ${p.pruned} superseded asset(s) from the backend.`);
                } else {
                    console.warn(`[save] asset prune returned ${pruneRes.status} (save itself succeeded).`);
                }
            }
        } catch (pruneErr) {
            console.warn('[save] asset prune failed (save itself succeeded):', pruneErr);
        }

        const sceneJsonUrl = `${API_BASE}/v/editor-scenes/${sceneId}/scene.json`;
        await events.invoke('showPopup', {
            type: 'info',
            header: justCreated ? 'Published to Olloport' : 'Saved to Olloport',
            message: justCreated
                ? `Created a new scene and uploaded it (id ${sceneId}). It's now live — open it in the viewer below.`
                : (wroteSource
                    ? 'Saved (markers, camera, thumbnail + splat source). Hard-reload the viewer to see the changes.'
                    : 'Saved (markers, camera, thumbnail). Splat source unchanged. Hard-reload the viewer to see the changes.'),
            link: `${VIEWER_BASE}/?sceneUrl=${encodeURIComponent(sceneJsonUrl)}`
        });
    } catch (err) {
        // A deliberate cancel is not a failure — inform, don't alarm.
        const cancelled = (err as Error)?.name === 'SaveCancelled';
        await events.invoke('showPopup', {
            type: cancelled ? 'info' : 'error',
            header: cancelled ? 'Save cancelled' : 'Save failed',
            message: err instanceof Error ? err.message : String(err)
        });
    } finally {
        events.fire('stopSpinner');
    }
};

/**
 * Startup hook: if opened with `?scene=<id>`, ensure the user is authenticated
 * then load that scene. No-op when there's no `?scene=` param.
 */
export const initOlloport = async (events: Events): Promise<void> => {
    // Any edit-history op (splat transform / delete / add / colour / rename) or
    // an LOD rebuild changes the splat source → the next save must re-upload the
    // .ssproj. EXCEPT pure selection ops ('selectAll'/'selectNone'/'selectInvert'
    // /'selectOp'), which are UI-only and not persisted — letting those trip the
    // flag would re-upload the source on every click and defeat the point.
    // Anything we don't recognise counts as source-dirtying (conservative: a
    // needless re-upload is merely slow, a missed one would silently lose edits).
    // Markers/camera/thumbnail/walk don't go through edit-history at all, so they
    // never reach here — that's what keeps their saves fast.
    // Ops that DON'T change the splat geometry source. Their result either rides
    // the cheap editor-meta.json sidecar (the whole-entity transform — see
    // buildDocMeta.splatXforms) or is selection-only, so they must NOT force a
    // full .ssproj re-upload. Everything else (delete, reset, per-gaussian
    // splatsTransform, colour, add, rename, hide/unhide) DOES dirty the source.
    // Op names are verified against edit-ops.ts.
    const SOURCE_CLEAN = new Set([
        'entityTransform', 'setPivot',
        'selectAll', 'selectNone', 'selectInvert', 'selectOp'
    ]);
    // A MultiOp is clean ONLY if every child is clean. The gizmo move fires a
    // MultiOp[entityTransform, setPivot] (clean); "Separate" fires a
    // MultiOp[deleteSelection, addSplat] (dirty). Conservative default: dirty —
    // a needless re-upload is merely slow, a missed one would lose an edit.
    const isSourceClean = (op: any): boolean => {
        if (!op || typeof op.name !== 'string') return false;
        if (op.name === 'multiOp' && Array.isArray(op.ops)) {
            return op.ops.length > 0 && op.ops.every(isSourceClean);
        }
        return SOURCE_CLEAN.has(op.name);
    };
    events.on('edit.add', (op: any) => {
        if (!isSourceClean(op)) sourceDirty = true;
    });
    events.on('lod.built', () => { sourceDirty = true; });

    // The document was reset or replaced (File ▸ New, opening another
    // .ssproj, a ply sequence) → whatever scene was bound is NOT this
    // document. Unbind, so the next save publishes as a new scene instead of
    // overwriting an unrelated one. openSceneFromBackend re-binds after its
    // own doc.load, so opening from the backend still saves in place.
    events.on('scene.clear', () => setOpenSceneId(null));

    const sceneId = params.get('scene');
    if (!sceneId) return;

    // Make sure we're signed in before pulling the scene — opens the in-editor
    // login dialog if needed (no bounce to app.olloport.com).
    if (!(await ensureSignedIn(events))) return;
    await openSceneFromBackend(events, sceneId);
};
